import React from "react";
import AccountStep from "../AccountStep";
import { getMember, signOut } from "../../../services/portalApi";

const HEARD = ["A friend or past guest", "Instagram", "Facebook", "Search engine", "Podcast or article", "A facilitator", "Cerro Tusa Springs", "Other"];

/**
 * Step 4 — each guest, and what the kitchen and the team need to know.
 * The lead guest is the account holder: they sign in with an emailed
 * code (no password), which is how they'll find this booking again.
 * Diets are select-all-that-apply; "No restrictions" clears the rest.
 * Medical screening is separate and comes after booking (spec 04).
 */
const StepDetails = ({ d, set, options, authed, onSignedIn }) => {
  const diets = options.diets;
  const exclusive = new Set(diets.filter((x) => x.exclusive).map((x) => x.id));

  const setGuest = (i, patch) => set((cur) => ({ people: cur.people.map((g, j) => (j === i ? { ...g, ...patch } : g)) }));
  const toggleDiet = (i, id) => set((cur) => ({
    people: cur.people.map((g, j) => {
      if (j !== i) return g;
      const has = g.diets.includes(id);
      if (has) return { ...g, diets: g.diets.filter((x) => x !== id) };
      return { ...g, diets: exclusive.has(id) ? [id] : [...g.diets.filter((x) => !exclusive.has(x)), id] };
    }),
  }));

  return (
    <section className="bk-step" aria-labelledby="s4">
      <h2 id="s4" className="bk-step-title">Guest details</h2>

      {!authed ? (
        <AccountStep onDone={onSignedIn}
          intro="The lead guest signs in with a code we email to them. It's how you'll find this booking, pay the balance and complete your screening." />
      ) : (
        <p className="bk-signed">
          Booking as <b>{getMember()?.email}</b>.{" "}
          <button type="button" className="bk-link" onClick={() => { signOut(); window.location.reload(); }}>Not you?</button>
        </p>
      )}

      {authed && d.people.map((g, i) => (
        <fieldset key={i} className="bk-panel bk-guest">
          <legend className="bk-guest-title">{i === 0 ? "Lead guest" : `Guest ${i + 1}`}</legend>
          <div className="mk-row">
            <label className="mk-field">
              <span>First name</span>
              <input value={g.first_name} onChange={(e) => setGuest(i, { first_name: e.target.value })} autoComplete={i === 0 ? "given-name" : "off"} required />
            </label>
            <label className="mk-field">
              <span>Last name</span>
              <input value={g.last_name} onChange={(e) => setGuest(i, { last_name: e.target.value })} autoComplete={i === 0 ? "family-name" : "off"} required />
            </label>
          </div>
          <div className="mk-row">
            <label className="mk-field">
              <span>Email {i > 0 && <em>optional</em>}</span>
              <input type="email" value={g.email} disabled={i === 0} onChange={(e) => setGuest(i, { email: e.target.value })} autoComplete={i === 0 ? "email" : "off"} />
            </label>
            <label className="mk-field">
              <span>Phone <em>optional</em></span>
              <input type="tel" value={g.phone} onChange={(e) => setGuest(i, { phone: e.target.value })} autoComplete={i === 0 ? "tel" : "off"} />
            </label>
          </div>
          <label className="mk-field">
            <span>Country of residence <em>optional</em></span>
            <input value={g.country} onChange={(e) => setGuest(i, { country: e.target.value })} autoComplete={i === 0 ? "country-name" : "off"} />
          </label>
          {i > 0 && <p className="bk-muted">With an email, this guest gets their own screening link and can see the booking from their account.</p>}

          <div className="bk-diets" role="group" aria-label={`Food for ${g.first_name || (i === 0 ? "the lead guest" : `guest ${i + 1}`)}`}>
            <p className="bk-label">Food <em>select all that apply</em></p>
            <div className="bk-chips">
              {diets.map((x) => (
                <label key={x.id} className={`bk-chip ${g.diets.includes(x.id) ? "is-on" : ""}`}>
                  <input type="checkbox" checked={g.diets.includes(x.id)} onChange={() => toggleDiet(i, x.id)} />
                  {x.name}
                </label>
              ))}
            </div>
          </div>
          <label className="mk-field">
            <span>Allergies and dietary needs <em>optional</em></span>
            <textarea rows={2} maxLength={1000} value={g.dietary_notes} onChange={(e) => setGuest(i, { dietary_notes: e.target.value })}
              placeholder="Severe nut allergy, no shellfish…" />
          </label>
        </fieldset>
      ))}

      {authed && (
        <>
          <div className="bk-panel bk-screening">
            <p className="bk-label">Medical screening</p>
            <p className="mb-0">
              {options.screeningNote || "Every guest completes a short, confidential medical screening after booking. It's included in the retreat price. Some experiences need medical clearance before they can be confirmed."}
            </p>
          </div>

          <fieldset className="bk-panel">
            <legend className="bk-label">Anything else</legend>
            <div className="mk-row">
              <label className="mk-field">
                <span>Mobility or accessibility needs <em>optional</em></span>
                <textarea rows={2} maxLength={1000} value={d.accessibility} onChange={(e) => set({ accessibility: e.target.value })} />
              </label>
              <label className="mk-field">
                <span>Room preferences <em>optional</em></span>
                <textarea rows={2} maxLength={1000} value={d.room_preferences} onChange={(e) => set({ room_preferences: e.target.value })} placeholder="Ground floor, quiet side…" />
              </label>
            </div>
            <label className="mk-field">
              <span>Anything we should know? <em>optional</em></span>
              <textarea rows={2} maxLength={2000} value={d.special_requests} onChange={(e) => set({ special_requests: e.target.value })} />
            </label>
            <label className="mk-field mk-field--narrow">
              <span>How did you hear about us? <em>optional</em></span>
              <select value={d.heard_from} onChange={(e) => set({ heard_from: e.target.value })}>
                <option value="">Choose one</option>
                {HEARD.map((h) => <option key={h} value={h}>{h}</option>)}
              </select>
            </label>
          </fieldset>
        </>
      )}
    </section>
  );
};

export default StepDetails;
